/**
 * Undo/Redo MVI State
 *
 * Architecture: MVI Pattern - State Layer
 */

import type { IApiError } from "@/shared/types";

// ============================================================================
// Domain State
// ============================================================================

export interface IUndoRedoDomainState {
  /** Edit session ID (server-side) */
  readonly sessionId: number | null;

  /** Client-generated UUID for this browser tab */
  readonly clientId: string | null;

  /**
   * Проект открытого сеанса; `null` — сеанса нет.
   *
   * Сеанс принадлежит одному проекту (edit-sessions-R04): смена проекта во
   * вкладке завершает его и начинает новый.
   */
  readonly sessionProjectId: number | null;

  /** Выбранный в шапке проект; `null` — выбор сброшен. */
  readonly selectedProjectId: number | null;

  /** Whether undo is available */
  readonly canUndo: boolean;

  /** Whether redo is available */
  readonly canRedo: boolean;

  /**
   * Выбрана ли фича в рабочем контексте.
   *
   * Отмена и возврат доступны **только при выбранной фиче**
   * (business-model-versioning-R21, основание: вердикт 232): без неё оба
   * действия заблокированы, а попытка выполнить сообщает «фича не выбрана».
   * Подстановки фичи по умолчанию нет.
   */
  readonly hasSelectedFeature: boolean;

  /**
   * Принадлежит ли выбранная фича проекту сеанса (undo-redo-R24).
   *
   * Смена проекта выбор фичи не сбрасывает (features-R10), поэтому страница
   * может показывать содержимое чужого для сеанса проекта; отмена там
   * недоступна — правки этого проекта к сеансу не привязываются
   * (changelog-R14) и отменяются из истории изменений своего проекта.
   */
  readonly isFeatureOfSelectedProject: boolean;

  /** Monotonically increasing counter, bumped after each successful undo/redo */
  readonly lastActionVersion: number;
}

// ============================================================================
// UI State
// ============================================================================

export interface IUndoRedoUIState {
  /** Whether an undo/redo operation is in progress */
  readonly isProcessing: boolean;

  /** Whether the session is being initialized */
  readonly isInitializing: boolean;

  /**
   * Отказ отмены или возврата, ожидающий показа пользователю
   * (undo-redo-R19): «нечего отменять», «нечего возвращать», «конфликт»,
   * «сеанс не найден», «сеанс закрыт», «отмена не удалась».
   *
   * Хранится в состоянии, а не показывается из эффекта: показ — дело слоя
   * отображения, а reducer остаётся чистым.
   */
  readonly pendingFailure: IApiError | null;
}

// ============================================================================
// Combined State
// ============================================================================

export interface IUndoRedoState {
  readonly domain: IUndoRedoDomainState;
  readonly ui: IUndoRedoUIState;
}

// ============================================================================
// Initial State Factory
// ============================================================================

export function createInitialDomainState(): IUndoRedoDomainState {
  return {
    sessionId: null,
    clientId: null,
    sessionProjectId: null,
    selectedProjectId: null,
    canUndo: false,
    canRedo: false,
    hasSelectedFeature: false,
    isFeatureOfSelectedProject: true,
    lastActionVersion: 0,
  };
}

export function createInitialUIState(): IUndoRedoUIState {
  return {
    isProcessing: false,
    isInitializing: false,
    pendingFailure: null,
  };
}

export function createInitialState(): IUndoRedoState {
  return {
    domain: createInitialDomainState(),
    ui: createInitialUIState(),
  };
}

// ============================================================================
// Selectors
// ============================================================================

/** Whether undo button should be enabled */
export function selectCanUndo(state: IUndoRedoState): boolean {
  return state.domain.canUndo && !state.ui.isProcessing;
}

/** Whether redo button should be enabled */
export function selectCanRedo(state: IUndoRedoState): boolean {
  return state.domain.canRedo && !state.ui.isProcessing;
}

/** Whether edit session is active */
export function selectHasSession(state: IUndoRedoState): boolean {
  return state.domain.sessionId !== null;
}

/**
 * Доступны ли отмена и возврат вообще — то же условие, по которому показан блок
 * кнопок (undo-redo-R11).
 *
 * Горячие клавиши подчиняются ему же: там, где продукт отмену не предлагает,
 * она не срабатывает и по клавише (undo-redo-R16). Иначе пользователь
 * откатывает вслепую — не видя ни состояния стека, ни результата.
 */
export function selectIsUndoRedoOffered(state: IUndoRedoState): boolean {
  // Сеанс принадлежит проекту, но отменять нечего, пока не выбрана фича:
  // отмена работает над её содержимым (versioning-R21).
  if (!selectHasSession(state) || !state.domain.hasSelectedFeature) return false;

  // Кнопки даёт только сеанс **выбранного** проекта: сеанс, оставшийся от
  // прежнего, отмены не предлагает — пользователь не может отменить действие
  // проекта, которого не видит (undo-redo-R11).
  if (
    state.domain.selectedProjectId !== null &&
    state.domain.sessionProjectId !== state.domain.selectedProjectId
  ) {
    return false;
  }

  // Показано содержимое чужого для сеанса проекта — отмена недоступна
  // (undo-redo-R24). Признаки «можно отменить/вернуть» при этом не
  // подменяются: они относятся к сеансу и приходят от продукта.
  return state.domain.isFeatureOfSelectedProject;
}

/** Отказ, ожидающий показа пользователю (undo-redo-R19). */
export function selectPendingFailure(state: IUndoRedoState): IApiError | null {
  return state.ui.pendingFailure;
}

/** Version counter bumped after each successful undo/redo */
export function selectLastActionVersion(state: IUndoRedoState): number {
  return state.domain.lastActionVersion;
}
